/**
 * Variant Media Filter Extension - multi-value tag support.
 *
 * The product-gallery filter mechanism (variantHasChanged in theme.js)
 * checks whether each cell's data-group-value EXACTLY matches the new
 * variant's option_value. This works for single-value tags but not for
 * multi-value tags (pipe-separated lists, e.g. "iPhone 13 Mini|iPhone 14 Pro|...").
 *
 * Verified 2026-05-08: the prior implementation (post-theme.js rAF cleanup
 * via Flickity.data(el).deactivate/activate) silently failed in production
 * because the theme bundles flickity-fade without exposing window.Flickity.
 * Flickity.data() returned null, the deactivate/activate was a no-op, and
 * cells un-filtered in the DOM never made it back into Flickity's internal
 * cell list -- so the carousel rendered with stale cell count, the main
 * image inflated to fill the gallery container, and showed white/blank for
 * every variant change after initial load. (Confirmed on Cleveland Browns
 * d9npm survivor.)
 *
 * New approach: pre-empt theme.js in the capture phase. For each multi-value
 * cell, temporarily rewrite data-group-value to a single value that either
 * matches or doesn't match the new variant -- so theme.js's exact-match
 * branch in variantHasChanged makes the right decision on its FIRST pass.
 * Restore the multi-value string in a microtask so the next variant change
 * sees the original. No Flickity manipulation needed; theme.js's own
 * deactivate/activate now sees correct is-filtered state on every cell.
 *
 * Why we use this approach: variant.media association is API-constrained
 * (productVariantAppendMedia is first-time-only), so we can't reliably
 * encode multi-variant-shared-media via variant.media. Encoding via the
 * variant_filter_map metafield with multi-value tags sidesteps that
 * entirely. See: cursor_plans/canonical_reference_layer_2026-05-03.md.
 */
(function () {
  'use strict';

  var SENTINEL_NO_MATCH = '__variant_filter_no_match__';

  function preemptMultiValue(variant) {
    if (!variant) return;

    // Collect option values lowercased; theme.js compares against
    // newVariant['option' + i].toLowerCase() so we match its normalization.
    var variantValues = [];
    for (var i = 1; i <= 3; i++) {
      var v = variant['option' + i];
      if (v) variantValues.push(String(v).toLowerCase());
    }
    if (!variantValues.length) return;

    var cells = document.querySelectorAll(
      '.product-gallery__carousel-item[data-group-value], ' +
      '.product-gallery__thumbnail[data-group-value]'
    );

    // Save original values keyed by cell so we can restore after theme.js
    // processes the change. Using a Map keeps the association even if
    // multiple variant:change events fire in quick succession.
    var saved = [];
    cells.forEach(function (cell) {
      var groupValue = cell.getAttribute('data-group-value') || '';
      if (groupValue.indexOf('|') === -1) return;  // single-value, theme.js handles natively

      var acceptedValues = groupValue.split('|').map(function (s) {
        return s.trim().toLowerCase();
      });
      var matchedValue = null;
      for (var j = 0; j < variantValues.length; j++) {
        if (acceptedValues.indexOf(variantValues[j]) !== -1) {
          matchedValue = variantValues[j];
          break;
        }
      }

      saved.push({ cell: cell, original: groupValue });
      // Rewrite to a single value that theme.js's exact-match will compare
      // correctly. If the multi-value list contained a match for this
      // variant, set to that match (theme.js compares lowercased on both
      // sides, so lowercase is fine). Otherwise set to a sentinel that
      // won't match anything, ensuring theme.js adds is-filtered.
      cell.setAttribute(
        'data-group-value',
        matchedValue || SENTINEL_NO_MATCH
      );
    });

    if (!saved.length) return;

    // Restore originals after theme.js's variantHasChanged actually
    // executes. theme.js wraps the mutation in fastdom.mutate, which
    // batches via requestAnimationFrame -- the work runs in the NEXT
    // animation frame, not synchronously. A microtask restore (verified
    // wrong 2026-05-08) runs before that rAF, so theme.js reads the
    // original multi-value string and the exact-match branch fails,
    // re-applying is-filtered to the cells we just un-pinned. Symptom:
    // shared assets disappear on every variant change after page load.
    //
    // setTimeout(0) queues a macrotask, which runs AFTER the rAF round
    // completes. By that point theme.js has applied its is-filtered
    // classes against our rewritten attribute and reactivated Flickity
    // with the correct cell set. We restore the original multi-value
    // string for the NEXT variant change to read fresh.
    setTimeout(function () {
      saved.forEach(function (entry) {
        entry.cell.setAttribute('data-group-value', entry.original);
      });
    }, 0);
  }

  // Capture phase fires before theme.js's bubble-phase listener, so our
  // attribute rewrites are in place when variantHasChanged reads them.
  document.addEventListener('variant:change', function (evt) {
    var variant = (evt.detail && evt.detail.variant) || null;
    if (!variant || !variant.id) return;
    preemptMultiValue(variant);
  }, true);

  // Initial page load: Liquid render already evaluated multi-value via the
  // inline {%- for av in accepted_values -%} block, so initial state is
  // correct without intervention.
})();
